import React, { useEffect, useState, useCallback, useMemo } from "react";

import LearnTextEmbed from "../../LearnPanelComponents/LearnCopy/LearnTextEmbed";

import Link from "@mui/material/Link";

type ReadMoreProps = {
  text: string | string[];
  embedLearn?: boolean;
  maxCharacters?: number;
  maxSections?: number;
  enterBeforeReadLess?: boolean;
  skipTerms?: string[];
};

function addEllipsis(text: string) {
  const trimmedString = text.trim();
  return trimmedString.endsWith(".") ? trimmedString + " " : trimmedString + "...";
}

function ReadMore(props: ReadMoreProps) {
  const textList = useMemo(() => (typeof props.text === "string" ? [props.text] : props.text), [props.text]);

  const totalCharacters = textList.reduce((acc, text) => acc + text.length, 0);

  const maxCharacters = props.maxCharacters ?? 100;
  const maxSections = props.maxSections ?? 1000;

  const tooLong = totalCharacters > maxCharacters || textList.length > maxSections;

  useEffect(() => {
    if (tooLong) {
      setIsExpanded(false);
    }
  }, [tooLong, textList]);

  const [isExpanded, setIsExpanded] = useState(!tooLong);

  const unclippedList = [];

  const embedLearn = props.embedLearn;
  const skipTerms = props.skipTerms;

  const addLearnTextEmbed = useCallback(
    (text: string, key: number) => {
      if (embedLearn === true) {
        return <LearnTextEmbed key={key} content={text} skipTerms={skipTerms} />;
      } else {
        return text;
      }
    },
    [embedLearn, skipTerms]
  );

  for (let i = 0; i < textList.length; i++) {
    if (i !== textList.length - 1) {
      unclippedList.push(addLearnTextEmbed(textList[i], i));
      unclippedList.push(<br key={"br" + i} />);
    } else if (tooLong) {
      unclippedList.push(addLearnTextEmbed(textList[i].trim() + " ", i));
      if (props.enterBeforeReadLess) {
        unclippedList.push(<br key={"br" + i} />);
      }
      unclippedList.push(
        <Link key="Less" component="button" onClick={() => setIsExpanded(false)}>
          Read Less
        </Link>
      );
    } else {
      unclippedList.push(addLearnTextEmbed(textList[i].trim(), i));
    }
  }

  let currentCharacterCount = 0;
  let clippedList = [];

  for (let i = 0; i < textList.length; i++) {
    if (currentCharacterCount + textList[i].length >= maxCharacters) {
      clippedList.push(addLearnTextEmbed(addEllipsis(textList[i].slice(0, maxCharacters - currentCharacterCount)), i));
      clippedList.push(
        <Link key="More" component="button" onClick={() => setIsExpanded(true)}>
          Read More
        </Link>
      );
      break;
    } else if (i === maxSections - 1) {
      clippedList.push(addLearnTextEmbed(addEllipsis(textList[i]), i));
      clippedList.push(
        <br key={"br" + i} />,
        <Link key="More" component="button" onClick={() => setIsExpanded(true)}>
          Read More
        </Link>
      );
      break;
    }
    clippedList.push(addLearnTextEmbed(textList[i], i));
    clippedList.push(<br key={"br" + i} />);
    currentCharacterCount += textList[i].length;
  }

  return <>{(isExpanded ? unclippedList : clippedList).map((text) => text)}</>;
}

export default ReadMore;
